/**
 * Shared user-click capture: ONE document-level capture-phase click listener that resolves the
 * clicked interactive control, computes its accessible label ONCE (elementLabel reads `innerText`,
 * which forces layout), and fans it out to every registered sink. Consumers register instead of each
 * installing their own listener and duplicating the selector/label logic:
 *  - lib/breadcrumbs — the error-report trail (always-on),
 *  - lib/session-actions — the wide-events session timeline (flag-gated; only registers when enabled).
 *
 * NEVER captures keystrokes or input values — only the control's accessible label. Best-effort:
 * telemetry must never break a real click.
 */

const MAX_LABEL_LEN = 120

export const INTERACTIVE_SELECTOR =
  'button, a, select, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"], input[type="checkbox"], input[type="radio"], input[type="submit"]'

/** The clicked control's accessible label — aria-label / title / visible text — never its value. */
export function elementLabel(el: Element): string {
  const aria = el.getAttribute('aria-label')?.trim()
  if (aria) return aria.slice(0, MAX_LABEL_LEN)
  const title = el.getAttribute('title')?.trim()
  if (title) return title.slice(0, MAX_LABEL_LEN)
  const rendered = (el as HTMLElement).innerText ?? el.textContent ?? ''
  const text = rendered.replace(/\s+/g, ' ').trim()
  return (text || el.tagName.toLowerCase()).slice(0, MAX_LABEL_LEN)
}

type ClickSink = (label: string) => void

const sinks = new Set<ClickSink>()
let listenerInstalled = false

/**
 * Register a sink for interactive-control clicks. The single shared listener is installed lazily on
 * the first registration. Client-only; no-op on the server. Registering the same sink twice is a no-op.
 */
export function registerClickSink(sink: ClickSink): void {
  if (typeof window === 'undefined') return
  sinks.add(sink)
  if (listenerInstalled) return
  listenerInstalled = true

  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target
        if (!(target instanceof Element)) return
        const control = target.closest(INTERACTIVE_SELECTOR)
        if (!control) return
        const label = elementLabel(control)
        for (const sinkFn of sinks) {
          try {
            sinkFn(label)
          } catch {
            // One sink failing must not starve the others — and never break a real click.
          }
        }
      } catch {
        // Never let telemetry break a real click.
      }
    },
    true,
  )
}
